/**
 * Tenant Detection Utilities for Multi-Domain Architecture
 *
 * These utilities handle domain-based tenant resolution for the multi-domain
 * architecture where each tenant has its own domain (e.g., gallo.shelflix.com.br).
 */

/**
 * Get the current domain for tenant resolution.
 * In local development, uses localStorage fallback.
 */
export function getTenantDomain(): string {
  const hostname = window.location.hostname;

  // Local development: use localStorage fallback
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return localStorage.getItem('devTenantDomain') || 'demo.shelflix.com.br';
  }

  return hostname;
}

/**
 * Check if running in local development mode.
 */
export function isLocalDevelopment(): boolean {
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1';
}

/**
 * Extract tenant slug from domain.
 * Used for API calls that need slug instead of full domain.
 *
 * @example
 * extractTenantSlug('hml.shelflix.com.br') // returns 'hml'
 * extractTenantSlug('gallo.shelflix.com.br') // returns 'gallo'
 * extractTenantSlug('app.shelflix.com.br') // returns 'heineken' (legacy redirect)
 * extractTenantSlug('pdv.clientx.com') // returns 'pdv.clientx.com' (custom domain)
 */
export function extractTenantSlug(domain: string): string {
  // Handle Shelflix subdomains
  if (domain.endsWith('.shelflix.com.br')) {
    const subdomain = domain.split('.')[0];
    // 'app' maps to 'heineken' (legacy domain redirect)
    // 'www' maps to 'default' tenant
    if (subdomain === 'app') {
      return 'heineken';
    }
    if (subdomain === 'www') {
      return 'default';
    }
    return subdomain;
  }

  // Custom domain - return full domain for DB lookup
  return domain;
}

/**
 * Generate URL for navigating to a different tenant (for master users).
 *
 * @example
 * getTenantUrl({ slug: 'gallo', domain: 'gallo.shelflix.com.br' }) // returns 'https://gallo.shelflix.com.br/'
 * getTenantUrl({ slug: 'client', domain: 'pdv.client.com' }, '/dashboard') // returns 'https://pdv.client.com/dashboard'
 */
export function getTenantUrl(tenant: { slug: string; domain?: string | null }, path: string = '/'): string {
  const domain = tenant.domain || `${tenant.slug}.shelflix.com.br`;
  return `https://${domain}${path}`;
}

/**
 * Check if the current domain is the HML (staging) environment.
 */
export function isHmlEnvironment(): boolean {
  const hostname = window.location.hostname;
  return hostname === 'hml.shelflix.com.br' || hostname === 'api.hml.shelflix.com.br';
}

/**
 * Get the API base URL for the current environment.
 * HML environment uses api.hml.shelflix.com.br, PRD uses api.shelflix.com.br
 */
export function getApiBaseUrl(): string {
  if (isLocalDevelopment()) {
    // In dev, use environment variable or default to localhost
    return import.meta.env.VITE_API_URL || 'http://localhost:3000';
  }

  if (isHmlEnvironment()) {
    return 'https://api.hml.shelflix.com.br';
  }

  return 'https://api.shelflix.com.br';
}
